import uniq from "lodash/uniq";

import { z } from "@reflag/shared/utils/zod";

import { APIResponse } from "./api";
import { EnvironmentSelectionQuerySchema } from "./environmentAPI";
import { CustomRuleSchema } from "./featureTargetingAPI";
import { PaginationQueryBaseSchema } from "./schemas/dataTableSchema";
import { ExternalIdArraySchema } from "./schemas/external";
import { ReflagUser } from "./types/ReflagUser";

export const FeatureConfigVariantSchema = z
  .object({
    key: z.string().min(1),
    payload: z.any().optional(),
  })
  .strict();

export type FeatureConfigVariant = z.output<typeof FeatureConfigVariantSchema>;

const FeatureConfigRuleSchema = z
  .object({
    segmentIds: z.array(z.string()),
    companyIds: ExternalIdArraySchema,
    userIds: ExternalIdArraySchema,
    customRules: z.array(CustomRuleSchema).nullish(),
    default: z.boolean(),
  })
  .strict();

export type FeatureConfigRule = z.output<typeof FeatureConfigRuleSchema>;

export type FeatureConfigVariantWithRule = {
  variant: FeatureConfigVariant;
  rule: FeatureConfigRule;
};

export type FeatureConfigVersion = {
  id: string;

  variants: FeatureConfigVariantWithRule[];

  version: number;
  currentVersion: boolean;
  changeDescription: string | null;

  createdAt: string;
  createdBy: ReflagUser | null;
};

export type FeatureConfig = {
  [environmentId: string]: FeatureConfigVersion;
};

const FeatureConfigVariantWithEnvironmentRulesSchema = z.object({
  variant: FeatureConfigVariantSchema,
  environmentRules: z.record(z.string().min(1), FeatureConfigRuleSchema),
});

export type FeatureConfigVariantWithEnvironmentRules = z.output<
  typeof FeatureConfigVariantWithEnvironmentRulesSchema
>;

export const UpdateFeatureConfigSchema = z.object({
  changeDescription: z.string().max(512).nullish(),
  variants: FeatureConfigVariantWithEnvironmentRulesSchema.array()
    .refine(
      (variants) => {
        if (!variants.length) {
          return true;
        }

        const environmentIds = uniq(
          variants.flatMap((v) => Object.keys(v.environmentRules)),
        );

        return environmentIds.every((envId) => {
          const rulesForEnv = variants
            .map((v) => v.environmentRules[envId])
            .filter(Boolean);

          return (
            rulesForEnv.length === 0 ||
            rulesForEnv.filter((r) => r.default).length === 1
          );
        });
      },
      {
        message:
          "One, and only one, rule must be set as default per environment",
      },
    )
    .refine(
      (variants) => {
        const keys = variants.map((v) => v.variant.key);
        return new Set(keys).size === keys.length;
      },
      { message: "Variant keys must be unique" },
    ),
});

export type UpdateFeatureConfig = z.input<typeof UpdateFeatureConfigSchema>;

export const GetFeatureConfigVersionsQuerySchema = PaginationQueryBaseSchema({
  pageSize: 50,
  sortOrder: "desc",
})
  .extend({
    sortBy: z.enum(["version"]).optional().default("version"),
  })
  .extend(EnvironmentSelectionQuerySchema.shape)
  .strict();

export type GetFeatureConfigVersionsQuery = z.input<
  typeof GetFeatureConfigVersionsQuerySchema
>;

export const UpdateFeatureConfigQuerySchema = z
  .object({
    expectedVersionIds: z.string().array().min(1).optional(),
  })
  .strict();

export type UpdateFeatureConfigQuery = z.input<
  typeof UpdateFeatureConfigQuerySchema
>;

export function featureConfigToUpdate(
  config: FeatureConfig,
): UpdateFeatureConfig {
  const variantsMap = new Map<
    string,
    FeatureConfigVariantWithEnvironmentRules
  >();

  // Process each environment's variants
  Object.entries(config).forEach(([envId, version]) => {
    version.variants.forEach(({ variant, rule }) => {
      if (!variantsMap.has(variant.key)) {
        variantsMap.set(variant.key, {
          variant,
          environmentRules: {},
        });
      }
      const variantWithRules = variantsMap.get(variant.key)!;
      variantWithRules.environmentRules[envId] = rule;
    });
  });

  return {
    changeDescription: "",
    variants: Array.from(variantsMap.values()),
  };
}

export interface FeatureConfigAPI {
  "/apps/:appId/features/:featureId/config": {
    GET: {
      response: APIResponse<FeatureConfig>;
      params: { appId: string; featureId: string };
    };
    PUT: {
      body: UpdateFeatureConfig;
      response: APIResponse<FeatureConfig>;
      params: { appId: string; featureId: string };
      query: UpdateFeatureConfigQuery;
    };
  };
  "/apps/:appId/features/:featureId/config/versions": {
    GET: {
      response: APIResponse<{ versions: FeatureConfigVersion[] }>;
      params: { appId: string; featureId: string };
      query: GetFeatureConfigVersionsQuery;
    };
  };
}
